import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import type SimplifiReportType from '@/Enums/ReportingDashboard/Simplifi/ReportType.enum';
import type { IBaseDataReport } from '@/Interfaces/ReportingDashboard/Simplifi/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { getWhitelabelOptions } from '@/helpers/filters/filterOptions';
import { getEffectiveFilters } from '@/helpers/reportingDashboard/filters';
import type { RootState } from '@/store';
import {
  selectReportingDashboardCurrentApiTabClients,
  selectReportingDashboardCurrentApiTabWhitelabels,
  selectReportingDashboardWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import { AsyncThunkPayloadCreator, isRejected } from '@reduxjs/toolkit';
import axios, { type AxiosError, HttpStatusCode } from 'axios';
import { getReportingFilter } from './filters';

export const SIMPLIFI_REPORTING_URL = '/reportingDashboard/simplifi/report';

const SIMPLIFI_MAX_RETRIES = 1;

/**
 * POST a Simplifi report request and return the JSON body.
 *
 * Mirrors `fetchEsmReport` for the same single-404-retry behavior, but lives
 * here (and not under `./esm`) so the Simplifi tab doesn't import a helper
 * named for the ESM (email/SMS/social) products. The retry exists to absorb
 * the brief 404 window between report-route registration and Cloud Run
 * cold-start; the Simplifi flow has the same window.
 */
async function postSimplifiReport<TBody, TReport>({
  signal,
  body,
  maxRetries = SIMPLIFI_MAX_RETRIES,
}: {
  signal?: AbortSignal;
  body: TBody;
  maxRetries?: number;
}): Promise<TReport | undefined> {
  let response;
  let attemptRetry = false;
  let retryCounter = 0;

  do {
    try {
      response = await axios.post(SIMPLIFI_REPORTING_URL, body, { signal });
      attemptRetry = false;
    } catch (error) {
      if ((error as AxiosError)?.response?.status !== HttpStatusCode.NotFound) {
        throw error;
      }
      attemptRetry = true;
      retryCounter++;
    }
  } while (retryCounter <= maxRetries && attemptRetry);

  return response?.data;
}

export const simplifiReportRequestHandler =
  <TReport = IBaseDataReport>(
    reportType: SimplifiReportType,
    fallbackValues: TReport | IBaseDataReport = {
      report: [],
    },
  ): AsyncThunkPayloadCreator<TReport, IAbortSignalThunkArg> =>
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      me: { role },
    } = state;

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);

    const filters = getEffectiveFilters(state, ApiTabIndex.Simplifi);

    const appliedFilter = getReportingFilter(filters, role!, clients, whitelabels);

    // Simplifi resolves data from whitelabelIds on the backend (via org hierarchy),
    // so we always include whitelabelIds even when no advertiser is explicitly selected.
    // When no whitelabel is selected, default to every whitelabel the role can
    // see — before the #3890 position→enum fix, getEffectiveFilters wrongly took
    // the foreign-tab branch here and injected this same default as a side
    // effect; without it the empty-filter guard below suppresses the request
    // entirely and the tab dead-ends on "no data" for admins.
    const selectedWhitelabels = filters.whitelabels?.length
      ? filters.whitelabels
      : getWhitelabelOptions({
          userRole: role!,
          whitelabels: selectReportingDashboardWhitelabels(state),
        });
    const whitelabelIds = (selectedWhitelabels ?? []).map((wl: { value: number }) => wl.value);

    if (!whitelabelIds.length && !appliedFilter.advertiserIds?.length && !appliedFilter.campaignIds?.length) {
      return fallbackValues as TReport;
    }

    const reportResponseData = await postSimplifiReport<unknown, TReport>({
      signal: payload?.signal,
      body: {
        reportType,
        filters: {
          ...appliedFilter,
          whitelabelIds,
        },
      },
    });

    return { ...((reportResponseData as object) ?? fallbackValues), filters } as TReport;
  };

/**
 * True when a settled thunk action is a genuine request failure. Excludes
 * thunks skipped by their `condition` (report already loaded) and requests
 * cancelled by requestSaver, which axios surfaces as `CanceledError`.
 */
export const isTransientRejection = (action: unknown) =>
  isRejected(action) && !action.meta.condition && action.error?.name !== 'CanceledError';

/**
 * Public wrapper around the Simplifi report POST, exposed for ad-hoc callers
 * outside the Redux-thunk path (e.g. the `window.loadSimplifiReports` tooling
 * the AI assistant uses to fan out a custom set of reports). Returns `undefined`
 * when the underlying call has nothing to return.
 */
export async function fetchSimplifiReport<TBody, TReport = unknown>(args: {
  signal?: AbortSignal;
  body: TBody;
  maxRetries?: number;
}): Promise<TReport | undefined> {
  return postSimplifiReport<TBody, TReport>(args);
}
